import { notEmpty, removeAtMedia, theme } from "@product/scmp-sdk";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { differenceInDays } from "date-fns";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";

import {
  ExcludedArticleEntityIdsInAIEngine,
  ExcludedArticleTypesInAIEngine,
  ExcludedSectionsInAIEngine,
} from "~/components/article/article-list/hooks/const";
import { isStyleSectionContent } from "~/components/section/section-style/helpers";
import { data } from "~/data";
import { acknowledgementGateArticleIds, article as articleData } from "~/data/article";
import { section as sectionData } from "~/data/section";
import { accountAtom } from "~/lib/account";
import { fetchAiEngineDataV2 } from "~/lib/ai-engine/apis";
import { AiEngineDataType } from "~/lib/ai-engine/enums";
import { sortArrayByReferenceOrder } from "~/lib/utils";
import type { articleRecommendationArticle$key } from "~/queries/__generated__/articleRecommendationArticle.graphql";
import type { articleRecommendationQuery } from "~/queries/__generated__/articleRecommendationQuery.graphql";

import type { ArticleRecommendation, PartialArticle } from "./types";

export const useArticleRecommendation = (article_: articleRecommendationArticle$key) => {
  const article = useFragment(
    graphql`
      fragment articleRecommendationArticle on Article {
        entityId
        moreOnThisArticles {
          entityId
          publishedDate
          sections {
            value {
              entityId
            }
          }
          types {
            value {
              entityId
            }
          }
          urlAlias
          urlRedirect {
            toUrl
          }
        }
      }
    `,
    article_,
  );

  const environment = useRelayEnvironment();
  const { user } = useAtomValue(accountAtom);

  const [{ result: articleRecommendation }, { execute: executeFetchRecommendedArticles }] =
    useAsync<ArticleRecommendation>(
      async () => {
        const aiEngineData = await fetchAiEngineDataV2({
          entityId: article.entityId,
          excludeIds: ExcludedArticleEntityIdsInAIEngine,
          excludedArticleTypes: ExcludedArticleTypesInAIEngine.map(element => element.name),
          excludedSections: ExcludedSectionsInAIEngine,
          limit: 20,
          type: AiEngineDataType.MoreOnThisTopic,
          userUuid: user?.uuid,
        });

        const recommendedArticles =
          aiEngineData.data.results &&
          (await fetchQuery<articleRecommendationQuery>(
            environment,
            graphql`
              query articleRecommendationQuery($entityIds: [String], $first: Int!) {
                articles(filter: { entityIds: $entityIds }, first: $first) {
                  edges {
                    node {
                      entityId
                      sections {
                        value {
                          entityId
                        }
                      }
                      types {
                        value {
                          entityId
                        }
                      }
                      urlRedirect {
                        toUrl
                      }
                    }
                  }
                }
              }
            `,
            {
              entityIds: aiEngineData.data.results.map(result => result.id) ?? [],
              first: aiEngineData.data.results.length ?? 0,
            },
            { fetchPolicy: "store-or-network" },
          ).toPromise());

        const orderedRecommendedArticles = sortArrayByReferenceOrder({
          data: {
            array: recommendedArticles?.articles.edges ?? [],
            idGetter: item => item.node.entityId,
          },
          order: { array: aiEngineData.data.results, idGetter: item => item.id },
        });

        const firstFilteredMoreOnThisArticle = article.moreOnThisArticles?.find(article => {
          if (!notEmpty(article)) return false;

          const isValidUrl = () => !article.urlAlias.match("http")?.length;

          const isExcludedPublishedDate = () => {
            if (!article.publishedDate) return false;
            return differenceInDays(new Date(), new Date(article.publishedDate)) > 30;
          };

          const isExplainerType = () => {
            const mainArticleType = first(article.types);
            if (!mainArticleType?.value) return false;
            return articleData.types.explainer.entityId === first(mainArticleType.value)?.entityId;
          };

          return (
            isValidUrl() &&
            !hasRedirectSetupToExternalDomain(article) &&
            !isAcknowledgementRequiredArticle(article) &&
            (!isExcludedPublishedDate() || isExplainerType()) &&
            !isExcludedSection(article) &&
            isV2SupportedArticle(article)
          );
        });

        const isMobileOnly = window.matchMedia(
          removeAtMedia(theme.breakpoints.only("mobile")),
        ).matches;

        const results = orderedRecommendedArticles.reduce<{
          infiniteScrollArticleIds: string[];
          moreOnThisTopicWidgetIds: string[];
        }>(
          (sortedArticleIds, { node: article }) => {
            // Note: Add additional check of isV2SupportedArticle here to ensure no
            //       non-migrated articles get included into list
            const canAddArticle =
              !(
                sortedArticleIds.infiniteScrollArticleIds.includes(article.entityId) ||
                sortedArticleIds.moreOnThisTopicWidgetIds.includes(article.entityId)
              ) && isV2SupportedArticle(article);

            const isWithinInfinityScrollCapacity =
              sortedArticleIds.infiniteScrollArticleIds.length < (isMobileOnly ? 5 : 2);

            const canAddToInfinityScroll =
              isWithinInfinityScrollCapacity &&
              !isAcknowledgementRequiredArticle(article) &&
              !isExcludedSection(article) &&
              !hasRedirectSetupToExternalDomain(article);

            if (canAddToInfinityScroll && canAddArticle) {
              sortedArticleIds.infiniteScrollArticleIds.push(article.entityId);
            } else if (canAddArticle) {
              sortedArticleIds.moreOnThisTopicWidgetIds.push(article.entityId);
            }

            return sortedArticleIds;
          },
          {
            infiniteScrollArticleIds: firstFilteredMoreOnThisArticle
              ? [firstFilteredMoreOnThisArticle.entityId]
              : [],
            moreOnThisTopicWidgetIds: [],
          },
        );

        return {
          firstFilteredMoreOnThisArticleEntityId: firstFilteredMoreOnThisArticle?.entityId,
          recommendationId: aiEngineData.data.rec_id,
          results,
        };

        function hasRedirectSetupToExternalDomain(article: PartialArticle) {
          if (!article.urlRedirect?.toUrl) return false;
          try {
            return new URL(article.urlRedirect.toUrl).host !== data.scmpDomains.SCMP;
          } catch {
            // handle the toUrl is relative url without proper host
            return false;
          }
        }

        function isExcludedSection(article: PartialArticle) {
          const mainSection = first(article.sections);
          if (!mainSection) return false;

          const excludedSectionEntityIds = new Set<string>([
            sectionData.photo.entityId,
            sectionData.video.entityId,
            sectionData.sport.racing.entityId,
          ]);

          return mainSection.value?.some(
            value => value && excludedSectionEntityIds.has(value.entityId),
          );
        }

        function isV2SupportedArticle(article: PartialArticle) {
          const isExcludedArticleType = () => {
            const mainArticleTypes = first(article.types);
            if (!mainArticleTypes?.value) return false;

            const excludedArticleTypeEntityIds = new Set<string>(
              ExcludedArticleTypesInAIEngine.map(element => element.entityId),
            );

            const firstArticleTypeValue = first(mainArticleTypes.value)?.entityId ?? "";
            const secondArticleTypeValue =
              mainArticleTypes.value.length > 1 ? mainArticleTypes.value[1]?.entityId ?? "" : "";

            return (
              excludedArticleTypeEntityIds.has(firstArticleTypeValue) ||
              excludedArticleTypeEntityIds.has(secondArticleTypeValue)
            );
          };

          const isStyleArticle = () => {
            const mainSection = first(article.sections);
            if (!mainSection) return false;

            const fullSectionsPathIds = mainSection?.value?.flatMap(s => s?.entityId ?? []) ?? [];
            return isStyleSectionContent(fullSectionsPathIds);
          };

          const isStarlingArticle = () =>
            ExcludedArticleEntityIdsInAIEngine.includes(article.entityId);

          return !isExcludedArticleType() && !isStyleArticle() && !isStarlingArticle();
        }

        function isAcknowledgementRequiredArticle(article: { entityId: string }) {
          return acknowledgementGateArticleIds.has(article.entityId);
        }
      },
      {
        firstFilteredMoreOnThisArticleEntityId: "",
        recommendationId: "",
        results: {
          infiniteScrollArticleIds: [],
          moreOnThisTopicWidgetIds: [],
        },
      },
    );

  useMountEffect(executeFetchRecommendedArticles);

  return { articleRecommendation };
};
